import { createHeadlessEditor } from '@lexical/headless';
import { RICH_TEXT_NODES } from '@plato/shared-util/lexical-config';
import { $createParagraphNode, $createTextNode, $getRoot, type SerializedEditorState } from 'lexical';

/**
 * Server-side utility to convert plain text to Lexical EditorState.
 *
 * Based on official Lexical documentation and GitHub discussions:
 * - https://lexical.dev/docs/concepts/editor-state
 * - https://github.com/facebook/lexical/discussions/3850
 *
 * This function takes plain text and converts it to proper Lexical JSON format,
 * handling line breaks by creating separate paragraph nodes.
 *
 * @param plainText - Plain text string (can contain \n line breaks)
 * @returns Promise<SerializedEditorState | null> - Lexical JSON state or null if no content
 */
export async function plainTextToLexical(plainText: string | null): Promise<SerializedEditorState | null> {
    if (!plainText?.trim()) {
        return null;
    }

    try {
        // Create headless editor with rich text nodes
        const editor = createHeadlessEditor({
            namespace: 'PlainTextConverter',
            nodes: RICH_TEXT_NODES,
            onError: (error) => {
                console.error('Plain text to Lexical conversion error:', error);
            },
        });

        // Convert plain text to Lexical using Promise-based approach
        return await new Promise<SerializedEditorState | null>((resolve, reject) => {
            try {
                // Register update listener to capture the final state
                const removeListener = editor.registerUpdateListener(({ editorState }) => {
                    // Clean up listener immediately
                    removeListener();
                    // Resolve with the JSON representation
                    resolve(editorState.toJSON());
                });

                // Perform the update with discrete: true for immediate execution
                editor.update(
                    () => {
                        const root = $getRoot();

                        // Clear any existing content
                        root.clear();

                        // Split text by line breaks to create separate paragraphs
                        const lines = plainText.split(/\r?\n/);

                        // Filter out empty lines at start/end, but preserve internal empty lines
                        const trimmedLines: string[] = [];
                        for (const [index, line] of lines.entries()) {
                            // Always include non-empty lines
                            if (line.trim()) {
                                trimmedLines.push(line);
                            }
                            // Include empty lines if they're between non-empty lines
                            else if (index > 0 && index < lines.length - 1) {
                                // Check if there are non-empty lines after this empty line
                                const hasContentAfter = lines.slice(index + 1).some((l) => l.trim());
                                if (hasContentAfter) {
                                    trimmedLines.push(''); // Preserve internal empty line
                                }
                            }
                        }

                        // If no meaningful content, create empty paragraph
                        if (trimmedLines.length === 0) {
                            const emptyParagraph = $createParagraphNode();
                            root.append(emptyParagraph);
                            return;
                        }

                        // Create paragraph nodes for each line
                        for (const line of trimmedLines) {
                            const paragraph = $createParagraphNode();

                            if (line.trim()) {
                                // Non-empty line: create text node
                                const textNode = $createTextNode(line);
                                paragraph.append(textNode);
                            }
                            // Empty line: paragraph remains empty (creates visual line break)

                            root.append(paragraph);
                        }
                    },
                    { discrete: true },
                ); // Force immediate execution
            } catch (error) {
                reject(error instanceof Error ? error : new Error(String(error)));
            }
        });
    } catch (error) {
        console.error('Error creating headless editor for plain text conversion:', error);
        return null;
    }
}
